import { NavLink, useLocation } from "react-router-dom";
import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { useAuth } from "../lib/auth";
import { useSkin } from "../lib/skin";
import { Wortmarke } from "./Wortmarke";
import { Assistent } from "./Assistent";
import {
  IconHaus, IconListe, IconBuch, IconBank, IconTropfen, IconUhr,
  IconAkte, IconDiagramm, IconSuche, IconAgent, IconAbmelden, IconWaage, IconBrief,
  IconDokument, IconPersonen, IconMahnung, IconAbrechnung, IconVerwaltung,
} from "./Symbole";

/**
 * The application shell.
 *
 * Three fixed regions and one that slides: a dark top bar, a dark navigator on the
 * left, the work surface, and the agent panel on the right. The chrome is dark and
 * the work surface is light so that the eye always knows which part of the screen is
 * furniture and which part is the customer's data — the same reason a spreadsheet
 * has grey row headers.
 *
 * The navigator is grouped by how the work actually arrives: things that are due
 * this month, the property files, the money, and the operational queues.
 */

interface NavEintrag {
  pfad: string;
  label: string;
  icon: (p: { size?: number }) => ReactNode;
}

const gruppen: { titel: string; eintraege: NavEintrag[] }[] = [
  {
    titel: "Überblick",
    eintraege: [
      { pfad: "/", label: "Periodenlauf", icon: IconUhr },
      { pfad: "/auswertungen", label: "Auswertungen", icon: IconDiagramm },
    ],
  },
  {
    titel: "Bestand",
    eintraege: [
      { pfad: "/liegenschaften", label: "Liegenschaften", icon: IconHaus },
      { pfad: "/bestandverhaeltnisse", label: "Bestandverhältnisse", icon: IconAkte },
      { pfad: "/personen", label: "Personen", icon: IconPersonen },
    ],
  },
  {
    titel: "Geld",
    eintraege: [
      { pfad: "/vorschreibungen", label: "Vorschreibungen", icon: IconBrief },
      { pfad: "/eingangsrechnungen", label: "Eingangsrechnungen", icon: IconDokument },
      { pfad: "/buchhaltung", label: "Buchhaltung", icon: IconBuch },
      { pfad: "/bank", label: "Bank & Zahlungen", icon: IconBank },
      { pfad: "/offene-posten", label: "Offene Posten", icon: IconWaage },
      { pfad: "/mahnwesen", label: "Mahnwesen", icon: IconMahnung },
      { pfad: "/abrechnungen", label: "Abrechnungen", icon: IconAbrechnung },
    ],
  },
  {
    titel: "Betrieb",
    eintraege: [
      { pfad: "/schaeden", label: "Schäden", icon: IconTropfen },
      { pfad: "/auftraege", label: "Aufträge & Vergabe", icon: IconListe },
      { pfad: "/fristen", label: "Fristen", icon: IconUhr },
      { pfad: "/dokumente", label: "Dokumente", icon: IconAkte },
      // Der Postausgang steht im Betrieb und nicht unter Verwaltung, weil er eine
      // tägliche Arbeitsmappe ist: was wartet auf Freigabe, was ist zurückgekommen.
      { pfad: "/postausgang", label: "Postausgang", icon: IconBrief },
    ],
  },
  {
    titel: "Verwaltung",
    eintraege: [
      { pfad: "/verwaltung", label: "Organisation", icon: IconVerwaltung },
      { pfad: "/einrichtung", label: "Einrichtung", icon: IconListe },
      { pfad: "/import", label: "Bestand importieren", icon: IconDokument },
      { pfad: "/hilfe", label: "Hilfe", icon: IconBuch },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { benutzer, abmelden } = useAuth();
  const { skin, setSkin } = useSkin();
  const [agentOffen, setAgentOffen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-dvh flex flex-col" style={{ background: "var(--surface-page)" }}>
      {/* Top bar */}
      <header
        className="h-[44px] shrink-0 flex items-center gap-[16px] px-[12px] border-b"
        style={{
          background: "var(--surface-chrome)",
          borderColor: "var(--line-chrome)",
          zIndex: "var(--z-actionbar)" as never,
        }}
      >
        <div className="w-[188px] shrink-0">
          <Wortmarke invers />
        </div>

        <button
          type="button"
          className="flex items-center gap-[6px] h-[26px] px-[8px] border text-[length:var(--text-xs)] rounded-[var(--radius-2)]"
          style={{
            background: "transparent",
            borderColor: "var(--line-chrome)",
            color: "var(--text-on-chrome-2)",
            minWidth: "260px",
          }}
        >
          <IconSuche size={13} />
          <span>Suchen …</span>
          <kbd
            className="ml-auto text-[length:var(--text-3xs)] px-[4px] py-[1px] rounded-[var(--radius-1)]"
            style={{ background: "var(--surface-chrome-hover)", color: "var(--text-on-chrome-2)" }}
          >
            Strg K
          </kbd>
        </button>

        <div className="ml-auto flex items-center gap-[4px]">
          <SkinUmschalter aktiv={skin} onWechsel={setSkin} />

          <button
            type="button"
            onClick={() => setAgentOffen((v) => !v)}
            aria-pressed={agentOffen}
            className="flex items-center gap-[6px] h-[26px] px-[8px] border text-[length:var(--text-xs)] rounded-[var(--radius-2)]"
            style={{
              background: agentOffen ? "var(--agent-accent)" : "transparent",
              borderColor: agentOffen ? "var(--agent-accent)" : "var(--line-chrome)",
              color: agentOffen ? "var(--text-on-chrome)" : "var(--text-on-chrome-2)",
            }}
          >
            <IconAgent size={13} />
            Assistent
          </button>

          <div className="w-px h-[20px] mx-[4px]" style={{ background: "var(--line-chrome)" }} />

          <span className="text-[length:var(--text-xs)]" style={{ color: "var(--text-on-chrome-2)" }}>
            {benutzer?.name}
          </span>
          <button
            type="button"
            onClick={() => void abmelden()}
            title="Abmelden"
            className="h-[26px] w-[26px] grid place-items-center rounded-[var(--radius-2)]"
            style={{ color: "var(--text-on-chrome-2)" }}
          >
            <IconAbmelden size={14} />
            <span className="sr-only">Abmelden</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Navigator */}
        <nav
          className="w-[188px] shrink-0 overflow-y-auto border-r py-[8px]"
          style={{ background: "var(--surface-chrome)", borderColor: "var(--line-chrome)" }}
          aria-label="Hauptnavigation"
        >
          {gruppen.map((gruppe) => (
            <div key={gruppe.titel} className="mb-[12px]">
              <p
                className="px-[12px] mb-[4px] text-[length:var(--text-3xs)]"
                style={{
                  color: "var(--text-on-chrome-3)",
                  textTransform: "uppercase",
                  letterSpacing: "var(--tracking-caps)",
                  fontWeight: "var(--weight-medium)",
                }}
              >
                {gruppe.titel}
              </p>
              {gruppe.eintraege.map((eintrag) => {
                const Icon = eintrag.icon;
                const aktiv =
                  eintrag.pfad === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(eintrag.pfad);
                return (
                  <NavLink
                    key={eintrag.pfad}
                    to={eintrag.pfad}
                    className={clsx(
                      "flex items-center gap-[8px] h-[26px] pl-[12px] pr-[8px]",
                      "text-[length:var(--text-sm)] border-l-2",
                    )}
                    style={{
                      color: aktiv ? "var(--text-on-chrome)" : "var(--text-on-chrome-2)",
                      background: aktiv ? "var(--surface-chrome-hover)" : "transparent",
                      borderLeftColor: aktiv ? "var(--accent-solid)" : "transparent",
                    }}
                  >
                    <Icon size={14} />
                    {eintrag.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Work surface */}
        <main className="flex-1 min-w-0 overflow-auto">{children}</main>

        {/* Agent panel */}
        {agentOffen ? (
          <aside
            className="w-[380px] shrink-0 border-l flex flex-col min-h-0"
            style={{ background: "var(--surface-raised)", borderColor: "var(--line-hairline)" }}
            aria-label="Assistent"
          >
            <Assistent />
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function SkinUmschalter({
  aktiv,
  onWechsel,
}: {
  aktiv: "modern" | "klassik";
  onWechsel: (s: "modern" | "klassik") => void;
}) {
  return (
    <div
      role="group"
      aria-label="Ansicht"
      className="flex border rounded-[var(--radius-2)] overflow-hidden"
      style={{ borderColor: "var(--line-chrome)" }}
    >
      {(["modern", "klassik"] as const).map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onWechsel(s)}
          aria-pressed={aktiv === s}
          className="h-[26px] px-[8px] text-[length:var(--text-2xs)]"
          style={{
            background: aktiv === s ? "var(--surface-chrome-hover)" : "transparent",
            color: aktiv === s ? "var(--text-on-chrome)" : "var(--text-on-chrome-3)",
            textTransform: "uppercase",
            letterSpacing: "var(--tracking-caps)",
          }}
        >
          {s === "modern" ? "Modern" : "Klassik"}
        </button>
      ))}
    </div>
  );
}

